"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useState } from "react";
import { FormField } from "../../features/forms/types";
import { Textfield } from "@/components/ui/form/Textfield";
import { Select } from "@/components/ui/form/Select";
import { Textarea } from "@/components/ui/form/Textarea";
import { Checkbox } from "@/components/ui/form/Checkbox";
import { Fileupload } from "@/components/ui/form/Fileupload";
import { Wysiwyg } from "@/components/ui/Wysiwyg";
import { ALLOWED_UPLOAD_EXTENSIONS, MAX_UPLOAD_MB, rejectUpload, UPLOAD_ACCEPT } from "@/features/forms/uploads";
import { useTranslations } from "@/lib/i18n/useTranslations";
interface FieldRendererProps {
  field: FormField;
  value: unknown;
  onChange: (value: unknown) => void;
  disabled?: boolean;
  color?: "purple" | "yellow" | "dark";
}

/**
 * Dynamically renders a form field based on the domain model.
 * Uses existing UI components where available.
 */
export const FieldRenderer: React.FC<FieldRendererProps> = t0 => {
  const $ = _c(56);
  const {
    field,
    value,
    onChange,
    disabled,
    color: t1
  } = t0;
  const color = t1 === undefined ? "purple" : t1;
  const {
    forms: t
  } = useTranslations();
  const [uploadError, setUploadError] = useState(null);
  const descriptionId = field.description ? `${field.name}-description` : undefined;
  const t2 = value as string | number | readonly string[] || "";
  let t3;
  if ($[0] !== onChange) {
    t3 = e => onChange(e.target.value);
    $[0] = onChange;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== color || $[3] !== descriptionId || $[4] !== disabled || $[5] !== field.label || $[6] !== field.name || $[7] !== field.placeholder || $[8] !== field.required || $[9] !== t2 || $[10] !== t3) {
    t4 = {
      id: field.name,
      name: field.name,
      label: field.label,
      required: field.required,
      placeholder: field.placeholder,
      "aria-describedby": descriptionId,
      disabled,
      color,
      value: t2,
      onChange: t3
    };
    $[2] = color;
    $[3] = descriptionId;
    $[4] = disabled;
    $[5] = field.label;
    $[6] = field.name;
    $[7] = field.placeholder;
    $[8] = field.required;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const commonProps = t4;
  let t5;
  bb0: switch (field.type) {
    case "text":
    case "email":
    case "phone":
    case "number":
      {
        const t6 = field.type === "phone" ? "tel" : field.type;
        let t7;
        if ($[12] !== commonProps || $[13] !== t6) {
          t7 = <Textfield {...commonProps} type={t6} />;
          $[12] = commonProps;
          $[13] = t6;
          $[14] = t7;
        } else {
          t7 = $[14];
        }
        t5 = t7;
        break bb0;
      }
    case "textarea":
      {
        let t6;
        if ($[15] !== commonProps) {
          t6 = <Textarea {...commonProps} />;
          $[15] = commonProps;
          $[16] = t6;
        } else {
          t6 = $[16];
        }
        t5 = t6;
        break bb0;
      }
    case "select":
      {
        let t6;
        if ($[17] !== field.defaultValue || $[18] !== field.options || $[19] !== t) {
          t6 = field.defaultValue ? field.options : [{
            label: t.hubspot.selectPlaceholder,
            value: ""
          }, ...(field.options ?? [])];
          $[17] = field.defaultValue;
          $[18] = field.options;
          $[19] = t;
          $[20] = t6;
        } else {
          t6 = $[20];
        }
        let t7;
        if ($[21] !== commonProps || $[22] !== t6) {
          t7 = <Select {...commonProps} options={t6} />;
          $[21] = commonProps;
          $[22] = t6;
          $[23] = t7;
        } else {
          t7 = $[23];
        }
        t5 = t7;
        break bb0;
      }
    case "file":
      {
        let fileProps;
        if ($[24] !== commonProps) {
          const {
            value: _value,
            placeholder: _placeholder,
            ...t6
          } = commonProps;
          fileProps = t6;
          $[24] = commonProps;
          $[25] = fileProps;
        } else {
          fileProps = $[25];
        }
        const t6 = value instanceof File ? value : null;
        const t7 = t.hubspot.upload.drag;
        let t8;
        if ($[26] !== t.hubspot.upload.maxSize) {
          t8 = t.hubspot.upload.maxSize.replace("{max}", String(MAX_UPLOAD_MB));
          $[26] = t.hubspot.upload.maxSize;
          $[27] = t8;
        } else {
          t8 = $[27];
        }
        const t9 = uploadError ?? undefined;
        let t10;
        if ($[28] !== onChange || $[29] !== t.hubspot.upload.tooLarge || $[30] !== t.hubspot.upload.type) {
          t10 = e_1 => {
            const picked = e_1.target?.files?.[0] ?? null;
            const rejection = picked ? rejectUpload(picked) : null;
            setUploadError(rejection === "too_large" ? t.hubspot.upload.tooLarge.replace("{max}", String(MAX_UPLOAD_MB)) : rejection === "type" ? t.hubspot.upload.type.replace("{allowed}", ALLOWED_UPLOAD_EXTENSIONS.join(", ")) : null);
            onChange(rejection ? null : picked);
          };
          $[28] = onChange;
          $[29] = t.hubspot.upload.tooLarge;
          $[30] = t.hubspot.upload.type;
          $[31] = t10;
        } else {
          t10 = $[31];
        }
        let t11;
        if ($[32] !== fileProps || $[33] !== t.hubspot.upload.choose || $[34] !== t.hubspot.upload.clear || $[35] !== t.hubspot.upload.drag || $[36] !== t10 || $[37] !== t6 || $[38] !== t8 || $[39] !== t9) {
          t11 = <Fileupload {...fileProps} accept={UPLOAD_ACCEPT} file={t6} dragLabel={t7} maxSizeLabel={t8} chooseLabel={t.hubspot.upload.choose} clearLabel={t.hubspot.upload.clear} error={t9} onChange={t10} />;
          $[32] = fileProps;
          $[33] = t.hubspot.upload.choose;
          $[34] = t.hubspot.upload.clear;
          $[35] = t.hubspot.upload.drag;
          $[36] = t10;
          $[37] = t6;
          $[38] = t8;
          $[39] = t9;
          $[40] = t11;
        } else {
          t11 = $[40];
        }
        t5 = t11;
        break bb0;
      }
    case "checkbox":
      {
        const t6 = !!value;
        let t7;
        if ($[41] !== onChange) {
          t7 = e_0 => onChange((e_0.target as HTMLInputElement).checked);
          $[41] = onChange;
          $[42] = t7;
        } else {
          t7 = $[42];
        }
        let t8;
        if ($[43] !== commonProps || $[44] !== t6 || $[45] !== t7) {
          t8 = <Checkbox {...commonProps} value={undefined} checked={t6} onChange={t7} />;
          $[43] = commonProps;
          $[44] = t6;
          $[45] = t7;
          $[46] = t8;
        } else {
          t8 = $[46];
        }
        t5 = t8;
        break bb0;
      }
    default:
      {
        let t6;
        if ($[47] !== commonProps) {
          t6 = <Textfield {...commonProps} type="text" />;
          $[47] = commonProps;
          $[48] = t6;
        } else {
          t6 = $[48];
        }
        t5 = t6;
      }
  }
  const control = t5;
  if (!field.description) {
    return control;
  }
  const helpIndent = field.type === "checkbox" ? "" : field.type === "select" ? "mx-4" : "mx-5";
  const t6 = `text-sm mt-xs opacity-70 ${helpIndent}`;
  let t7;
  if ($[49] !== descriptionId || $[50] !== field.description || $[51] !== t6) {
    t7 = <Wysiwyg html={field.description} mode="body" id={descriptionId} className={t6} />;
    $[49] = descriptionId;
    $[50] = field.description;
    $[51] = t6;
    $[52] = t7;
  } else {
    t7 = $[52];
  }
  let t8;
  if ($[53] !== control || $[54] !== t7) {
    t8 = <>{control}{t7}</>;
    $[53] = control;
    $[54] = t7;
    $[55] = t8;
  } else {
    t8 = $[55];
  }
  return t8;
};